import React, { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StandardButtonLoading } from "@/components/ui/standard-loading";
import { Family, Parent, BillingStatus } from "@/types/models";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { FamilyService } from "@/services/familyService";

const familyWithParentFormSchema = z.object({
  familyName: z.string().min(1, "Family name is required"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().optional().refine((val) => !val || z.string().email().safeParse(val).success, {
    message: "Invalid email address",
  }),
  phone: z.string().optional(),
});

type FamilyWithParentFormValues = z.infer<typeof familyWithParentFormSchema>;

interface AddFamilyWithParentDialogProps {
  open: boolean;
  onClose: () => void;
  onFamilyWithParentAdded: (family: Family, parent: Parent, families: Family[]) => void;
}

const AddFamilyWithParentDialog: React.FC<AddFamilyWithParentDialogProps> = ({
  open,
  onClose,
  onFamilyWithParentAdded,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, organizationId } = useAuth();

  const form = useForm<FamilyWithParentFormValues>({
    resolver: zodResolver(familyWithParentFormSchema),
    defaultValues: {
      familyName: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
  });

  useEffect(() => {
    const familyName = form.watch('familyName');
    if (familyName) {
      form.setValue('lastName', familyName);
    }
  }, [form.watch('familyName')]);

  const onSubmit = async (data: FamilyWithParentFormValues) => {
    console.log("[AddFamilyWithParentDialog] onSubmit called", { familyName: data.familyName, organizationId, isSubmitting });
    if (isSubmitting) return; // Prevent double submit
    setIsSubmitting(true);
    if (!organizationId) {
      toast.error("Organization ID not found");
      setIsSubmitting(false);
      return;
    }

    // Check for duplicate family name before submitting
    const existingFamilies = await FamilyService.getFamilies(organizationId);
    console.log("[AddFamilyWithParentDialog] Existing families:", existingFamilies.map(f => f.name));
    const duplicate = existingFamilies.find(f => f.name.trim().toLowerCase() === data.familyName.trim().toLowerCase());
    if (duplicate) {
      toast.error("A family with this name already exists.", {
        description: "Please add a distinguishing detail (e.g., first name or initial) to the family name."
      });
      setIsSubmitting(false);
      return;
    }

    console.log("[AddFamilyWithParentDialog] Submitting new family:", { familyName: data.familyName, organization_id: organizationId });
    try {
      // Use the combined operation from FamilyService
      const { family, parent } = await FamilyService.createFamilyWithParent(
        {
          name: data.familyName,
          organization_id: organizationId,
          billing_status: 'current' as BillingStatus
        },
        {
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email || null,
          phone: data.phone || null,
          organization_id: organizationId,  // Add organization_id to the parent data
          is_primary: true,
          invite_status: 'pending'
        }
      );

      // Force refetch of families after add
      const updatedFamilies = await FamilyService.getFamilies(organizationId);
      onFamilyWithParentAdded(family, parent, updatedFamilies);
      toast.success("Family with parent created successfully");
      form.reset();
      onClose();
    } catch (error: any) {
      if (error?.code === '23505') {
        // Retry fetch up to 3 times with delay to handle slow DB propagation
        let exists = false;
        for (let i = 0; i < 3; i++) {
          await new Promise(res => setTimeout(res, 300));
          const updatedFamilies = await FamilyService.getFamilies(organizationId);
          exists = updatedFamilies.some(f => f.name.trim().toLowerCase() === data.familyName.trim().toLowerCase());
          if (exists) break;
        }
        if (!exists) {
          toast.error("A family with this name already exists.", {
            description: "Please add a distinguishing detail (e.g., first name or initial) to the family name."
          });
        }
        // If it exists, do nothing (the add succeeded)
      } else {
        console.error("Error creating family with parent:", error);
        toast.error("Failed to create family with parent");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Family with Parent</DialogTitle>
          <DialogDescription>
            Create a new family with a parent in your organization.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-4">
              <h3 className="text-sm font-medium">Family Information</h3>
              <FormField
                control={form.control}
                name="familyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Family Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter family name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-sm font-medium">Parent Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter first name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter last name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="Enter email address"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter phone number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <StandardButtonLoading 
                type="submit" 
                loading={isSubmitting}
                loadingText="Creating..."
              >
                Create Family with Parent
              </StandardButtonLoading>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddFamilyWithParentDialog;
